/* eslint-disable max-lines-per-function */
import {
  InfiniteData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { RangeHorizon } from '@toggle/chart';
import { TabLinesProps } from '@toggle/design-system';
import { getTimeTillEndOfDayMs } from '@toggle/helpers';
import {
  FilterResultMethod,
  FilterResults,
  MappedEntity,
  screener,
  TSDatum,
} from '@toggle/toggle';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { ActiveFilterOptions } from '~/components/filters/Filters';
import { FilterGroups } from '~/hooks/use-filter-actions/useFilterActions';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useSubscription } from '~/stores/use-subscription/useSubscription';
import { wretchRequest } from '~/utils/api-fetch/apiFetch';
import { hasEmptyEntityTagValues } from '~/widgets/scenario/hooks/use-screener-entities-tags/entity-screener-service/utils';

import { mapFilters } from '../use-screener-store/filter-utils';
import { useScreenerStore } from '../use-screener-store/useScreenerStore';
import { SCREENER_ASSET_COLUMN_ID } from './screener-events-table/utils/column-utils';
import { ScreenerEvents } from './screener-events-table/utils/types';
import {
  ColumnMetaWithKey,
  DEFAULT_TAB,
  FilterQueryResultData,
  getCategoryData,
  getNewTableTab,
  getNumDaysForHorizon,
  getOrderedMetaFields,
  getPinnedColumnsMeta,
  KEY_DEV_TAB,
  PREV_DAY_RETURN_COLUMN_META_VALUE_FIELD,
  PRICE_COLUMN_META_VALUE_FIELD,
  SCREENER_INSIGHTS_CATALYSTS_COLUMN_ID,
  SCREENER_INSIGHTS_SCENARIO_COLUMN_ID,
  TableData,
  tableDataQueryUpdater,
} from './screener-table-utils';
import { fetchTickerCellData } from './utils';

export type ScreenerTableData = {
  entity: MappedEntity;
  ts: TSDatum[] | undefined;
} & Record<string, FilterResultMethod>;

export type UseScreenerTableReturn = ReturnType<typeof useScreenerTable>;

export const QUERY_KEY_SCREENER_RESULTS = 'QUERY_KEY_SCREENER_RESULTS';
const QUERY_KEY_TABLE_DATA = 'QUERY_KEY_TABLE_DATA';
const PAGE_SIZE = 15;

export const useScreenerTable = ({
  filters,
  horizon,
}: {
  filters: ActiveFilterOptions[];
  horizon: RangeHorizon;
}) => {
  const {
    filterGroups,
    handlePinnedColumns,
    handleColumnOrdering,
    handleColumnSorting,
    tableState: { sorting, columnOrdering, pinnedColumns },
    resetTableState,
    hasTableChanges,
    activeSavedScreen,
    customDefaultTableTab,
    setCustomDefaultTableTab,
  } = useScreenerStore();
  const [tab, setTab] = useState(customDefaultTableTab ?? DEFAULT_TAB);
  const kgOptions = filterGroups['Knowledge Graph Filters'].activeFilterOptions;

  const prevFilterGroups =
    useRef<FilterGroups<'Knowledge Graph Filters' | 'Market Filters'>>(
      undefined
    );

  const queryClient = useQueryClient();
  const { trackScreenerTabSelected } = usePosthogEvents();
  const staleTime = getTimeTillEndOfDayMs();
  const nDays = getNumDaysForHorizon(horizon);

  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );

  const columnsToHide = [
    PRICE_COLUMN_META_VALUE_FIELD,
    PREV_DAY_RETURN_COLUMN_META_VALUE_FIELD,
  ];

  const requestFilters = useMemo(() => mapFilters(filters), [filters]);

  const filterResultQueryResult = useInfiniteQuery<FilterQueryResultData>({
    queryKey: [QUERY_KEY_SCREENER_RESULTS, requestFilters, sorting],
    queryFn: async ({ signal, pageParam }) => {
      const filterResults = await wretchRequest<FilterResults>(
        screener.postFilter.path,
        {
          method: 'post',
          body: {
            filters: requestFilters,
            page: {
              number: pageParam,
              size: PAGE_SIZE,
            },
            ...(sorting.length && {
              sort_by: {
                field: sorting[0].id,
                desc: sorting[0].desc,
              },
            }),
          },
          signal,
        }
      );

      const tickerColumnData = await fetchTickerCellData({
        tags: filterResults.entities,
        staleTime,
        nDays,
        queryClient,
      });
      return {
        filterResults,
        tickerColumnData,
      };
    },
    initialPageParam: 1,
    staleTime,
    enabled:
      !!Object.keys(requestFilters).length &&
      !hasEmptyEntityTagValues(requestFilters),
    getNextPageParam: lastData => lastData.filterResults.page.next,
  });

  const categoryData = getCategoryData(filterResultQueryResult.data, tab);
  const shouldShowCatalysts = !isBasicOrLightUser;
  const defaultTabOrdering = categoryData
    ? [
      SCREENER_ASSET_COLUMN_ID,
      ...(shouldShowCatalysts ? [SCREENER_INSIGHTS_CATALYSTS_COLUMN_ID] : []),
      SCREENER_INSIGHTS_SCENARIO_COLUMN_ID,
      ...categoryData.meta.map(item => item.field),
    ]
    : undefined;

  const isTableChanged = hasTableChanges(tab, defaultTabOrdering);

  const updateColumnsState = ({
    data,
    tab,
    resetToDefault,
  }: {
    data: InfiniteData<FilterQueryResultData> | undefined;
    tab: string;
    resetToDefault?: boolean;
  }) => {
    // Do not update column state if it is a screener event
    const screenerEventValues: string[] = Object.values(ScreenerEvents);

    if (screenerEventValues.includes(tab) || tab === KEY_DEV_TAB) {
      return;
    }

    const latestData = data?.pages[data.pages.length - 1];

    if (!latestData || !latestData.filterResults.entities.length) {
      return;
    }

    const categoryData = latestData.filterResults.data.find(d => d.key === tab);
    const defaultPinnedCols =
      activeSavedScreen?.config.tableState.pinnedColumns ?? {};
    const pinnedColumnsMeta = getPinnedColumnsMeta({
      filterResults: data.pages[0]?.filterResults,
      pinnedColumns: resetToDefault ? defaultPinnedCols : pinnedColumns,
      categoryData,
    });
    const queryKey = [QUERY_KEY_TABLE_DATA, requestFilters, sorting];
    queryClient.setQueryDefaults(queryKey, {
      gcTime: Infinity,
    });
    queryClient.setQueryData<TableData>(
      queryKey,
      tableDataQueryUpdater({ data, tab, pinnedColumnsMeta })
    );

    const meta: ColumnMetaWithKey[] = [
      ...(categoryData?.meta.map(item => ({ ...item, key: tab })) ?? []),
      ...pinnedColumnsMeta,
    ];

    const defaultColumnOrder =
      activeSavedScreen?.config.tableState.columnOrdering[tab];
    const columnOrder = columnOrdering[tab];
    const newOrder = getOrderedMetaFields({
      columnOrder,
      meta,
      resetToDefault,
      defaultColumnOrder,
      isCatalystInsightsEnabled: !isBasicOrLightUser,
      isEarningsInsightsEnabled: !isBasicOrLightUser,
    });
    const shouldUpdateOrder =
      resetToDefault ||
      !columnOrder?.length ||
      newOrder.some(id => !columnOrder.includes(id));

    if (shouldUpdateOrder) {
      handleColumnOrdering({
        tab,
        fields: newOrder,
      });
    }
  };

  useLayoutEffect(() => {
    prevFilterGroups.current = undefined;

    return () => {
      setCustomDefaultTableTab();
      setTab(DEFAULT_TAB);
    };
  }, [activeSavedScreen]);

  useLayoutEffect(() => {
    const haveKGFiltersChanged = !!kgOptions.length;

    if (haveKGFiltersChanged) {
      const newSort = sorting.filter(
        sort =>
          !(
            sort.id.includes('theme_exposure') ||
            sort.id.includes('country_exposure')
          )
      );
      handleColumnSorting(newSort);
    }
  }, [kgOptions]);

  //important, make all state updates with the queryClient.setQueryData at once
  useLayoutEffect(() => {
    if (!filterResultQueryResult.data) {
      return;
    }

    const categoryData = getCategoryData(filterResultQueryResult.data, tab);

    const newTab = getNewTableTab({
      categoryData,
      prevFilterGroups: prevFilterGroups.current,
      filterGroups,
    });
    prevFilterGroups.current = filterGroups;

    setTab(newTab);
    updateColumnsState({
      data: filterResultQueryResult.data,
      tab: newTab,
    });

    // Set up the default tab in case the user saves a draft initialized with a working list
    if (tab !== DEFAULT_TAB) {
      const defaultCategoryData = getCategoryData(
        filterResultQueryResult.data,
        DEFAULT_TAB
      );
      const defaultTab = getNewTableTab({
        categoryData: defaultCategoryData,
        prevFilterGroups: prevFilterGroups.current,
        filterGroups,
      });
      updateColumnsState({
        data: filterResultQueryResult.data,
        tab: defaultTab,
      });
    }
  }, [filterResultQueryResult.data]);

  const onTabChange: TabLinesProps<string>['onChange'] = (_, tab) => {
    trackScreenerTabSelected(POSTHOG_TRACKING_SOURCES.SCREENER, tab);
    setTab(tab);
    updateColumnsState({ data: filterResultQueryResult.data, tab });
  };

  const onPinChange = (columnsMeta: ColumnMetaWithKey[], pin: boolean) => {
    handlePinnedColumns({
      columnsMeta,
      pin,
    });
  };

  const onColumnOrderChange = (fields: string[]) => {
    handleColumnOrdering({ tab, fields });
  };

  const { data: tableData } = useQuery<TableData>({
    queryKey: [QUERY_KEY_TABLE_DATA, requestFilters, sorting],
    staleTime: Infinity,
    enabled: !!filterResultQueryResult.data,
    queryFn: async () => {
      // we'll return the cached data if available, otherwise throw to avoid unneeded fetch
      const cached = queryClient.getQueryData<TableData>([
        QUERY_KEY_TABLE_DATA,
        requestFilters,
        sorting,
      ]);

      if (!cached) {
        throw new Error('No cached table data available');
      }

      return cached;
    },
  });

  const resetTable = () => {
    resetTableState(tab);
    const shouldResetToDefault =
      !activeSavedScreen?.config.tableState.columnOrdering[tab] ||
      tab == DEFAULT_TAB;
    updateColumnsState({
      data: filterResultQueryResult.data,
      tab,
      resetToDefault: shouldResetToDefault,
    });
  };

  const filterResults = filterResultQueryResult.data?.pages[0]?.filterResults;
  const pinnedColumnsMeta =
    filterResults &&
    getPinnedColumnsMeta({
      filterResults,
      pinnedColumns,
      categoryData,
    });
  const columnsMeta: ColumnMetaWithKey[] = (
    categoryData
      ? [
        ...categoryData.meta.map(item => ({ ...item, key: tab })),
        ...(pinnedColumnsMeta || []),
      ]
      : []
  ).filter(c => !columnsToHide.includes(c.field));

  return {
    filterResultQueryResult,
    tableData,
    onTabChange,
    activeTab: tab,
    sorting,
    handleColumnSorting,
    columnsMeta,
    columnOrdering,
    pinnedColumns,
    onColumnOrderChange,
    onPinChange,
    resetTable,
    isTableChanged,
  };
};
